import { Avatar, Button, Form, Input, Select, Switch, Tag } from "antd";
import { CloseOutlined, UserOutlined } from "@ant-design/icons";
import {
  formatAssignmentRange,
  getLocalDateString,
  isAssignmentInactiveByDate,
  resolveProfilePhotoUrl,
} from "../../../utils/generalUtils";

const AdminTableForm = ({
  form,
  formFields,
  action, // "add" | "edit"
  loading,
  onSubmit,
  onMarkDeleteAssignment,
  positionData,
  tableData,
  isEmployee,
  employeePhoto,
}) => {
  const watchedPositions = Form.useWatch("positions", form) || [];

  const renderTextInput = (field) => (
    <Input
      placeholder={field.placeholder}
      disabled={field.disabled || (action === "edit" && field.disabledEdit)}
    />
  );

  const renderSelect = (field) => (
    <Select
      placeholder={`Select ${field.label}`}
      options={field.selectItems?.map((item) => ({
        label: item.label,
        value: item.name,
      }))}
      disabled={field.disabled || (action === "edit" && field.disabledEdit)}
      popupMatchSelectWidth={false}
    />
  );

  const renderSwitch = (field) => (
    <Switch disabled={field.disabled || (action === "edit" && field.disabledEdit)} />
  );

  const renderPositionSelectFromTableData = (field) => (
    <Select
      showSearch
      placeholder={`Select ${field.label}`}
      options={tableData?.map((item) => ({
        label: item.position_name,
        value: item.id,
      }))}
      filterOption={(input, option) =>
        option?.label?.toLowerCase().includes(input.toLowerCase())
      }
      filterSort={(a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      }
      disabled={field.disabled || (action === "edit" && field.disabledEdit)}
      popupMatchSelectWidth={false}
    />
  );

  const renderEmployeePositionGroup = (field) => (
    <Form.Item key={field.name} label={field.label}>
      <Form.List name={field.name}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => {
              const rowData =
                watchedPositions?.[name] ||
                form.getFieldValue([field.name, name]) ||
                {};
              const statusIsInactive = isAssignmentInactiveByDate(
                rowData?.position_end_date,
                getLocalDateString()
              );

              return (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    marginBottom: 16,
                    borderBottom: "1px solid #f0f0f0",
                    paddingBottom: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "position_id"]}
                      rules={[
                        {
                          required: true,
                          message: "Position is required",
                        },
                      ]}
                      style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
                    >
                      <Select
                        showSearch
                        placeholder="Select Position"
                        optionLabelProp="label"
                        options={positionData}
                        filterOption={(input, option) =>
                          option?.label
                            ?.toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        filterSort={(a, b) =>
                          a.label.toLowerCase().localeCompare(b.label.toLowerCase())
                        }
                        popupMatchSelectWidth={false}
                      />
                    </Form.Item>
                    <Button
                      type="primary"
                      shape="circle"
                      icon={<CloseOutlined />}
                      danger
                      onClick={() => {
                        if (onMarkDeleteAssignment) {
                          onMarkDeleteAssignment(rowData?.position_assignment_id);
                        }
                        remove(name);
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "position_start_date"]}
                      rules={[
                        {
                          validator: async (_, value) => {
                            const endDate = form.getFieldValue([
                              field.name,
                              name,
                              "position_end_date",
                            ]);

                            if (value && endDate && endDate < value) {
                              throw new Error(
                                "End date must be on or after start date."
                              );
                            }
                          },
                        },
                      ]}
                      style={{ minWidth: 165, marginBottom: 0 }}
                    >
                      <Input type="date" placeholder="Start Date" />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "position_end_date"]}
                      rules={[
                        {
                          validator: async (_, value) => {
                            const startDate = form.getFieldValue([
                              field.name,
                              name,
                              "position_start_date",
                            ]);

                            if (startDate && value && value < startDate) {
                              throw new Error(
                                "End date must be on or after start date."
                              );
                            }
                          },
                        },
                      ]}
                      style={{ minWidth: 165, marginBottom: 0 }}
                    >
                      <Input type="date" placeholder="End Date" />
                    </Form.Item>

                    <Tag color={statusIsInactive ? "red" : "green"}>
                      {statusIsInactive ? "Inactive" : "Active"}
                    </Tag>
                  </div>

                  <div style={{ color: "#666", fontSize: 12 }}>
                    Assignment Date:{" "}
                    {formatAssignmentRange(
                      rowData?.position_start_date,
                      rowData?.position_end_date
                    )}
                  </div>

                  <Form.Item
                    {...restField}
                    name={[name, "position_assignment_id"]}
                    hidden
                  >
                    <Input />
                  </Form.Item>
                </div>
              );
            })}

            <Button
              type="dashed"
              onClick={() =>
                add({
                  position_assignment_id: null,
                  position_id: undefined,
                  position_start_date: null,
                  position_end_date: null,
                })
              }
              block
            >
              Add Position
            </Button>
          </>
        )}
      </Form.List>
    </Form.Item>
  );

  const renderField = (field) => {
    if (field.type === "group") {
      return renderEmployeePositionGroup(field);
    }

    const commonProps = {
      hidden: field.hidden,
      key: field.name,
      name: field.name,
      label: field.label,
      rules: field.rules,
    };

    return (
      <Form.Item
        {...commonProps}
        valuePropName={field.type === "switch" ? "checked" : undefined}
      >
        {field.type === "select"
          ? renderSelect(field)
          : field.type === "position-select"
          ? renderPositionSelectFromTableData(field)
          : field.type === "switch"
          ? renderSwitch(field)
          : renderTextInput(field)}
      </Form.Item>
    );
  };

  const renderedFields = formFields?.map((field) => renderField(field));

  return (
    <Form
      form={form}
      layout="horizontal"
      labelCol={{ span: 7 }}
      labelAlign="left"
      onFinish={onSubmit}
    >
      {isEmployee && action === "edit" && (
        <div style={{ marginBottom: 20 }}>
          <Avatar
            size={110}
            src={
              resolveProfilePhotoUrl(employeePhoto) ||
              `${process.env.PUBLIC_URL}/assets/avatar.png`
            }
            icon={<UserOutlined />}
          />
        </div>
      )}

      {renderedFields}

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          {action === "add" ? "Add" : "Save Changes"}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default AdminTableForm;
